import { css } from 'aphrodite';
import * as React from 'react';
import { USER_STATUS_LABELS, USER_STATUS_OPTIONS } from '../../../hooks/useUserFilters';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownItem,
	DropdownSeparator,
	DropdownTrigger,
} from '../../components-v2/Dropdown';
import { grayIconFill } from '../../styles/colors';
import { TextInput } from '../TextInput';
import { styleSheet as dropdownStyles } from '../dataBoards/DropdownComponent/styles';
import { DisclosureIcon } from '../svgs/icons/DisclosureIcon';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { userFiltersStyles } from './styles';

/**
 * Props for the UserFilters component
 */
interface UserFiltersProps {
	// Filter state
	filters: {
		statuses: string[];
		roles: string[];
		searchText: string;
	};
	// Handlers
	handleStatusFilterChange: (status: string) => void;
	handleRoleFilterChange: (role: string) => void;
	handleSearchTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	clearSearchText: () => void;
	resetStatusFilter: () => void;
	resetRoleFilter: () => void;

	// Messages
	statusDropdownMessage: string;
	roleDropdownMessage: string;

	// Role configuration
	roleOptions: readonly string[];
	roleLabels: Record<string, string>;

	// User count
	userCount?: number;
}

/**
 * UserFilters Component
 *
 * A reusable component that provides user filtering UI including:
 * - Multi-select dropdown for user status filtering (Active, Created, Deactivated)
 * - Multi-select dropdown for role filtering (configurable based on user type)
 * - Text search input for filtering by name or email
 * - Real-time user count display showing current filter results
 *
 * This component is designed to work with the useUserFilters hook to provide
 * a complete filtering solution for user management pages.
 *
 * @param props - Component props including filter state, handlers, and configuration
 * @returns JSX element containing the filter UI controls
 *
 * @example
 * ```tsx
 * <UserFilters
 *   {...userFilters}
 *   roleOptions={['admin', 'employee']}
 *   roleLabels={{ admin: 'Administrator', employee: 'Employee' }}
 *   userCount={filteredUsers.length}
 *   styleSheet={styleSheet}
 * />
 * ```
 */
export const UserFilters: React.FC<UserFiltersProps> = ({
	filters,
	handleStatusFilterChange,
	handleRoleFilterChange,
	handleSearchTextChange,
	clearSearchText,
	resetStatusFilter,
	resetRoleFilter,
	statusDropdownMessage,
	roleDropdownMessage,
	roleOptions,
	roleLabels,
	userCount,
}) => {
	return (
		<>
			<div className={css(userFiltersStyles.filtersContainer)}>
				<div className={css(userFiltersStyles.filterRow)}>
					<div className={css(userFiltersStyles.filterGroup)}>
						<span className={css(userFiltersStyles.filterLabel)}>Status:</span>
						<Dropdown>
							<DropdownTrigger
								render={props => {
									return (
										<div className={css(userFiltersStyles.dropdownAnchor)} {...props}>
											<span>{statusDropdownMessage}</span>
											<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
										</div>
									);
								}}
							/>
							<DropdownContent>
								{USER_STATUS_OPTIONS.map(status => (
									<DropdownCheckboxItem
										key={`status-select-${status}`}
										checked={filters.statuses.includes(status)}
										onCheckedChange={() => handleStatusFilterChange(status)}
									>
										{USER_STATUS_LABELS[status]}
									</DropdownCheckboxItem>
								))}
								<DropdownSeparator />
								<DropdownItem onClick={resetStatusFilter}>Reset Filter</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</div>
					<div className={css(userFiltersStyles.filterGroup)}>
						<span className={css(userFiltersStyles.filterLabel)}>Role:</span>
						<Dropdown>
							<DropdownTrigger
								render={props => {
									return (
										<div className={css(userFiltersStyles.dropdownAnchor)} {...props}>
											<span>{roleDropdownMessage}</span>
											<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
										</div>
									);
								}}
							/>
							<DropdownContent>
								{roleOptions.map(role => (
									<DropdownCheckboxItem
										key={`role-select-${role}`}
										checked={filters.roles.includes(role)}
										onCheckedChange={() => handleRoleFilterChange(role)}
									>
										{roleLabels[role]}
									</DropdownCheckboxItem>
								))}
								<DropdownSeparator />
								<DropdownItem onClick={resetRoleFilter}>Reset Filter</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</div>
					<div className={css(userFiltersStyles.filterGroup, userFiltersStyles.searchGroup)}>
						<TextInput
							className={css(userFiltersStyles.searchInputWrapper)}
							inputClassName={css(userFiltersStyles.searchInput)}
							leftAccessory={<SearchIcon className={css(userFiltersStyles.searchIcon)} fillColor={grayIconFill} />}
							type='text'
							inputId='search-users'
							onChange={handleSearchTextChange}
							value={filters.searchText}
							placeholder='Search by name or email...'
							rightAccessory={
								filters.searchText ? (
									<button className={css(userFiltersStyles.clearSearchButton)} onClick={clearSearchText}>
										Clear
									</button>
								) : null
							}
						/>
					</div>
					{userCount !== undefined && (
						<div className={css(userFiltersStyles.userCountIndicator)}>
							Showing {userCount} user{userCount !== 1 ? 's' : ''}
						</div>
					)}
				</div>
			</div>
		</>
	);
};
